import * as Yup from "yup";
import { Form, Formik } from "formik";
import TextInput from "../FormInputs/TextInput2";
import { TailwindSpinner } from "../../utils/tailwindSpinner";
import { FaArrowRight } from "react-icons/fa6";
import toast from "react-hot-toast";
import { userServices } from "../../services/user";

interface PayoutInfoFormProps {
  userId: string;
  onSuccess: () => void;
}

const validationSchema = Yup.object({
  nextOfKinName: Yup.string()
    .trim()
    .min(2, "Enter a valid name")
    .max(255)
    .required("Next of kin name is required"),
  nextOfKinRelationship: Yup.string()
    .trim()
    .min(2, "Enter a valid relationship")
    .max(100)
    .required("Relationship is required"),
  nextOfKinPhone: Yup.string()
    .trim()
    .min(7, "Enter a valid phone number")
    .max(20)
    .required("Phone number is required"),
  nextOfKinEmail: Yup.string()
    .trim()
    .email("Enter a valid email")
    .required("Next of kin email is required"),
  nextOfKinAddress: Yup.string()
    .trim()
    .min(5, "Enter a valid mailing address")
    .max(500)
    .required("Mailing address is required"),
  interacEmail: Yup.string()
    .trim()
    .email("Enter a valid email")
    .required("Your Interac e-Transfer email is required"),
});

export default function PayoutInfoForm({
  userId,
  onSuccess,
}: PayoutInfoFormProps) {
  const initialValues = {
    nextOfKinName: "",
    nextOfKinRelationship: "",
    nextOfKinPhone: "",
    nextOfKinEmail: "",
    nextOfKinAddress: "",
    interacEmail: "",
  };

  return (
    <div>
      <h1 className="text-center font-bold text-lg">
        Complete Your Personal Information and Payout Details
      </h1>
      <div className="text-center md:w-[500px]">
        <p className="mb-3 text-sm text-[#5A5C5E]">
          Kindly update your information to include your next of kin and the
          Interac email your payouts should go to. This only takes a minute
          and you only need to do it once.
        </p>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await userServices.payoutInfo.submit(userId, values);
            toast.success("Payout details saved. Thank you!");
            onSuccess();
          } catch (error: any) {
            toast.error(
              error?.response?.data?.message ||
                "Failed to save payout details",
            );
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, isValid }) => {
          return (
            <Form className="max-h-[70vh] overflow-y-auto flex flex-col gap-4 my-3 md:w-[500px]">
              <p className="font-semibold text-sm text-[#2B2C34] -mb-2">
                Next of Kin
              </p>
              <TextInput
                name="nextOfKinName"
                label="Full Name"
                required
              />
              <TextInput
                name="nextOfKinRelationship"
                label="Relationship to You"
                placeholder="e.g. Spouse, Sibling, Parent"
                required
              />
              <TextInput
                name="nextOfKinPhone"
                type="tel"
                label="Phone Number"
                required
              />
              <TextInput
                name="nextOfKinEmail"
                type="email"
                label="Email Address"
                required
              />
              <TextInput
                name="nextOfKinAddress"
                label="Mailing Address"
                required
              />

              <p className="font-semibold text-sm text-[#2B2C34] mt-2 -mb-2">
                Interac e-Transfer
              </p>
              <TextInput
                name="interacEmail"
                type="email"
                label="Email to send your Interac e-Transfer to"
                required
              />

              <div className="flex justify-end mt-3 w-full">
                <button
                  disabled={!isValid || isSubmitting}
                  type="submit"
                  className="flex justify-center items-center border-[1px] bg-primary rounded-[8px] py-3 px-4 md:w-[220px] text-white font-semibold gap-3 disabled:bg-opacity-50 disabled:cursor-not-allowed"
                >
                  Save Details
                  {isSubmitting ? <TailwindSpinner /> : <FaArrowRight />}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
